import { useCallback } from 'react';
import { SelectChangeEvent } from '@mui/material';
import type { 
  FilterBarState, 
  FilterBarMode,
  CapacityValues 
} from '../../../types/components/filter_bar_types';
import { 
  clearCapacityFilter, 
  updateDateTimeFilter 
} from '../utils';

export interface FilterBarHandlers {
  // Date/time handlers
  onChangeEventDate: (date: any) => void;
  onChangeStartTime: (evt: SelectChangeEvent<number>) => void;
  onChangeEndTime: (evt: SelectChangeEvent<number>) => void;
  clearStartTime: () => void;
  clearEndTime: () => void;
  
  // Capacity handlers
  onSubmitCapacityFilter: (values: CapacityValues) => void;
  onCancelCapacityFilter: () => void;
  
  // Mode-specific handlers
  onClearFilters?: () => void; // Only for search mode
}

export interface UseFilterBarOptions {
  state: FilterBarState;
}

export interface UseFilterBarResult extends FilterBarHandlers {
  // Props to pass directly to FilterBar
  mode: FilterBarMode;
  locationQuery?: string;
  onChangeLocationQuery?: (query: string) => void;
  eventDate: any;
  startTime: number | null;
  endTime: number | null;
  capacityFilterValues: CapacityValues;
  expressBookSettings?: any;
  hoursOfOperation?: any;
}

export const useFilterBar = ({ state }: UseFilterBarOptions): UseFilterBarResult => {
  const {
    mode,
    dateTimeFilter,
    setDateTimeFilter,
    capacityFilterValues,
    setCapacityFilterValues,
    locationQuery,
    onChangeLocationQuery,
    expressBookSettings,
    hoursOfOperation,
  } = state;

  // Date/time handlers
  const onChangeEventDate = useCallback((date: any) => {
    setDateTimeFilter(updateDateTimeFilter(dateTimeFilter, { date }));
  }, [dateTimeFilter, setDateTimeFilter]);

  const onChangeStartTime = useCallback((evt: SelectChangeEvent<number>) => {
    setDateTimeFilter(updateDateTimeFilter(dateTimeFilter, { 
      startOffset: Number(evt.target.value) 
    }));
  }, [dateTimeFilter, setDateTimeFilter]);

  const onChangeEndTime = useCallback((evt: SelectChangeEvent<number>) => {
    setDateTimeFilter(updateDateTimeFilter(dateTimeFilter, { 
      endOffset: Number(evt.target.value) 
    }));
  }, [dateTimeFilter, setDateTimeFilter]);

  const clearStartTime = useCallback(() => {
    setDateTimeFilter(updateDateTimeFilter(dateTimeFilter, { 
      startOffset: null,
      endOffset: null
    }));
  }, [dateTimeFilter, setDateTimeFilter]);

  const clearEndTime = useCallback(() => {
    setDateTimeFilter(updateDateTimeFilter(dateTimeFilter, { 
      startOffset: null,
      endOffset: null
    }));
  }, [dateTimeFilter, setDateTimeFilter]);

  // Capacity handlers
  const onSubmitCapacityFilter = useCallback((values: CapacityValues) => {
    setCapacityFilterValues(values);
  }, [setCapacityFilterValues]);

  const onCancelCapacityFilter = useCallback(() => {
    setCapacityFilterValues(clearCapacityFilter());
  }, [setCapacityFilterValues]);

  const onClearFilters = useCallback(() => {
    setDateTimeFilter(null);
    setCapacityFilterValues(clearCapacityFilter());
  }, [setDateTimeFilter, setCapacityFilterValues]);

  return {
    // Mode and configuration
    mode,
    
    // Search mode props
    locationQuery,
    onChangeLocationQuery,
    
    // Date/time props
    eventDate: dateTimeFilter?.date || null,
    startTime: dateTimeFilter?.startOffset || null,
    endTime: dateTimeFilter?.endOffset || null,
    
    // Capacity props
    capacityFilterValues,
    
    // Profile mode props
    expressBookSettings,
    hoursOfOperation,
    
    // Event handlers
    onChangeEventDate,
    onChangeStartTime,
    onChangeEndTime,
    clearStartTime,
    clearEndTime,
    onSubmitCapacityFilter,
    onCancelCapacityFilter,
    onClearFilters,
  };
};
